import {useNavigate, useSearchParams} from "react-router-dom";
import {DataRequestParams, DataRequestState, DataTable, useDataRequest, useDebouncedFilter} from "../../data";
import FilterTableSchedulers, {SchedulersFilter} from "./FilterTableSchedulers";
import {useMemo} from "react";
import {schedulersStyles} from "../../../styles/schedulers/schedulers.styles";
import {useSchedulersHandling} from "../../../hooks/schedulers/useSchedulersHandling";
import {Box, Button, capitalize, Paper, Typography} from "@mui/material";
import {Scheduler, SchedulerScope} from "../../../api";
import Layout from "../../Layout";
import {GridColDef} from "@mui/x-data-grid";
import {permissions} from "../../../permissions";
import MultipleGuard from "../../guards/MultipleGuard";
import ButtonDeleteScheduler from "../ButtonDeleteScheduler";

interface TableSchedulersProps {
    scope?: SchedulerScope;
    mandantId?: string;
    aufgabeId?: string;
    embedded?: boolean;
}

export default function TableSchedulers({scope, mandantId, aufgabeId, embedded}: TableSchedulersProps) {
    const [searchParams] = useSearchParams();
    const {translation, loadSchedulers, loadSchedulersByOwner, scopeLabel, canDeleteScheduler} = useSchedulersHandling();
    const navigate = useNavigate();
    const isFixedOwner = scope !== undefined || mandantId !== undefined || aufgabeId !== undefined;

    const request: DataRequestState<SchedulersFilter> = useDataRequest<SchedulersFilter>({
        filter: {
            filtered: searchParams.get("filter") ?? "",
            search: searchParams.get("search") ?? "",
        },
    });

    const [{ search }, setField] = useDebouncedFilter(request);

    const filter = useMemo(() => {
        return searchParams.get("filter");
    }, [searchParams]);

    const table = (
        <Paper sx={schedulersStyles.tableSchedulers.paper}>
            <Box sx={schedulersStyles.tableSchedulers.headerBoxWrapper}>
                <Typography variant="h5">
                    {capitalize(translation('schedulers'))}
                </Typography>
                <MultipleGuard
                    permissions={[
                        permissions.schedulers.create,
                        permissions.schedulers.own_create,
                    ]}
                >
                    <Button
                        variant={"contained"}
                        color={"secondary"}
                        onClick={() => {
                            const params = new URLSearchParams();
                            if (mandantId) params.set("mandantId", mandantId);
                            if (aufgabeId) params.set("aufgabeId", aufgabeId);
                            const query = params.toString();
                            navigate(`/schedulers/new${query ? `?${query}` : ""}`);
                        }}
                    >
                        {capitalize(translation('scheduler-create'))}
                    </Button>
                </MultipleGuard>
            </Box>
            <FilterTableSchedulers setField={setField} search={search}/>
            <Box sx={schedulersStyles.tableSchedulers.box}>
                <DataTable<Scheduler, SchedulersFilter>
                    name="table-schedulers"
                    queryKey={["schedulers", scope ?? "", mandantId ?? "", aufgabeId ?? ""]}
                    loadData={(params: DataRequestParams<SchedulersFilter>) => isFixedOwner
                        ? loadSchedulersByOwner(params, request.filter, {scope, mandantId, aufgabeId})
                        : loadSchedulers(params, request.filter)}
                    request={request}
                    setField={setField}
                    columns={useColumns(scopeLabel, translation, canDeleteScheduler)}
                    size="large"
                    initialFilters={filter ? JSON.parse(filter) : undefined}
                    onRowClick={(row) => navigate(`/schedulers/${row.id}`)}
                    disableRowSelectionOnClick={true}
                />
            </Box>
        </Paper>
    );

    if (embedded) return table;
    return <Layout>{table}</Layout>;
}

function useColumns(
    scopeLabel: (scope: Scheduler['scope']) => string,
    translation: (key: string, options?: Record<string, unknown>) => string,
    canDeleteScheduler: (scheduler: Scheduler) => boolean,
): Array<GridColDef<Scheduler>> {
    return useMemo(
        () => [
            {
                field: "name",
                headerName: capitalize(translation("scheduler-attributes-name")),
                flex: 1,
            },
            {
                field: "scope",
                headerName: capitalize(translation("scheduler-attributes-scope")),
                flex: 1,
                valueFormatter: (value) => value ? scopeLabel(value) : "",
            },
            {
                field: "mandantId",
                headerName: capitalize(translation("scheduler-attributes-mandant")),
                flex: 1,
            },
            {
                field: "usedByAufgabenCount",
                headerName: capitalize(translation("scheduler-attributes-used-by-aufgaben")),
                flex: 1,
                valueFormatter: (value) => value ?? 0,
            },
            {
                field: "actions",
                type: "actions",
                headerName: capitalize(translation("delete")),
                sortable: false,
                flex: 0.5,
                renderCell: (params) => {
                    const scheduler = params.row as Scheduler;
                    const inUse = (scheduler.usedByAufgabenCount ?? 0) > 0;
                    const canDelete = canDeleteScheduler(scheduler);
                    if (!canDelete) return null;
                    return (
                        <Box onClick={(e) => e.stopPropagation()}>
                            <ButtonDeleteScheduler
                                schedulerId={scheduler.id}
                                disabled={inUse}
                                disabledReason={inUse ? translation('scheduler-delete-disabled-in-use', {count: scheduler.usedByAufgabenCount}) : undefined}
                            />
                        </Box>
                    );
                },
            },
        ],
        [translation, scopeLabel, canDeleteScheduler]
    );
}
